import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthContext';
import { ApiError } from '@/api/client';
import { invites as invitesApi } from '@/api/invites';
import ErrorBanner from '@/components/ErrorBanner';
import Spinner from '@/components/Spinner';

/**
 * Two flows enter here:
 *   - Installer flow: `?installationId=...` — the user just installed the GitHub App and is
 *     claiming the new Organization as OWNER.
 *   - Invite flow: `?inviteToken=...` — the user is accepting a team invite (email locked).
 *
 * Plain `/signup` without either is an error: registration requires one of these context
 * tokens (we don't allow open self-signup).
 */
export default function SignupPage() {
  const [params] = useSearchParams();
  const installationId = params.get('installationId') ? Number(params.get('installationId')) : null;
  const inviteToken = params.get('inviteToken');

  const navigate = useNavigate();
  const { register, isAuthenticated, user } = useAuth();

  const invitePreview = useQuery({
    queryKey: ['invite', inviteToken],
    queryFn: () => invitesApi.preview(inviteToken!),
    enabled: !!inviteToken,
    retry: false,
  });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // For invite flow, prefill (and lock) email to the invited address.
  useEffect(() => {
    if (invitePreview.data) setEmail(invitePreview.data.email);
  }, [invitePreview.data]);

  // Already logged-in user landing here: bounce to dashboard.
  useEffect(() => {
    if (isAuthenticated && user) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, user, navigate]);

  if (!installationId && !inviteToken) {
    return (
      <div className="centered-full login-card">
        <div className="login-inner">
          <h1>Sign up</h1>
          <p className="muted">
            Self-signup isn't available. Install the GitHub App to start, or use the invite link in
            your email.
          </p>
          <Link to="/login" className="btn btn-ghost">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  if (inviteToken && invitePreview.isLoading) {
    return (
      <div className="centered-full">
        <Spinner />
      </div>
    );
  }

  if (inviteToken && invitePreview.isError) {
    return (
      <div className="centered-full login-card">
        <div className="login-inner">
          <h1>Invite expired</h1>
          <p className="muted">
            This invite link is invalid or has expired. Ask the person who invited you to send a new one.
          </p>
          <Link to="/login" className="btn btn-ghost">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await register({
        email: email.trim(),
        password,
        name: name.trim() || null,
        installationId,
        inviteToken,
      });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(humanize(err));
    } finally {
      setSubmitting(false);
    }
  };

  const title = inviteToken
    ? `Join ${invitePreview.data?.organizationName ?? 'your team'}`
    : 'Create your account';
  const subtitle = inviteToken
    ? `You were invited as ${invitePreview.data?.role.toLowerCase() ?? 'member'}.`
    : 'You just installed the GitHub App. Sign up to claim your organization.';

  return (
    <div className="centered-full login-card">
      <div className="login-inner">
        <h1>{title}</h1>
        <p className="muted">{subtitle}</p>
        <form className="form-grid" onSubmit={handleSubmit}>
          {error ? <ErrorBanner error={error} /> : null}
          <label>
            <span>Email</span>
            <input
              className="input"
              type="email"
              autoComplete="email"
              required
              readOnly={!!inviteToken}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            <span>Name (optional)</span>
            <input
              className="input"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label>
            <span>Password (min 8 chars)</span>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !email || password.length < 8}
          >
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        <div className="login-links">
          <Link to="/login">Already have an account? Sign in</Link>
        </div>
      </div>
    </div>
  );
}

function humanize(err: unknown): Error {
  if (err instanceof ApiError) {
    const code = typeof err.body === 'object' && err.body && 'error' in err.body
      ? (err.body as { error?: string }).error
      : undefined;
    const message = {
      email_already_registered: 'An account with that email already exists. Try signing in.',
      invite_email_mismatch: "The invite was sent to a different email — use that address.",
      invalid_or_expired_token: 'The invite link is invalid or expired.',
      registration_requires_installation_or_invite:
        'Open the signup page from the GitHub install callback or your invite email.',
    }[code ?? ''];
    return new Error(message ?? err.message);
  }
  return err instanceof Error ? err : new Error('Sign up failed.');
}
